/**
* @copyright Copyright (c) MITSUBISHI Electric Corporation. All rights reserved.
*/

import React, { createContext, useState, useEffect, useCallback, useReducer } from "react";
import * as CognitoService from "./aws/cognito";
import * as AccountHttp from "./http/accountHttp"
import * as TenantHttp from "./http/tenantHttp"
import PAGES from "./consts/pages";
export const IndexContext = createContext();

export default function IndexProvider({ children }) {
    const [jwtToken, setJwtToken] = useState(undefined)
    const [message, setMessage] = useState({ variant: "Light", msg: "", show: false })
    const [loginDetails, setLoginDetails] = useState({})
    const [accountDetails, setAccountDetails] = useState(undefined)
    const [tenantList, setTenantList] = useState([])
    const [accountList, setAccountList] = useState([])
    const [selectedTenant, setSelectedTenant] = useState(undefined)
    const [selectedAccount, setSelectedAccount] = useState(undefined)
    const [breadcrumbs, setBreadcrumbs] = useState([{ title: "トップ", page: PAGES.MENUS }])
    const [pageLoading, setPageLoading] = useState(true)
    const [currentChangeAccountId, setCurrentChangeAccountId] = useState(undefined)
    const [currentPage, setCurrentPage] = useState(PAGES.LOGIN)
    const [userAttributes, setUserAttributes] = useState(undefined)
    const [cognitoUser, setCognitoUser] = useState(undefined)

    const getAccountDetails = async () => {
        const onFailure = () => {
            setAccountDetails(undefined)
            setMessage({ variant: "Warning", msg: "アカウント情報を取得できませんでした。", show: true })
        }

        try {
            setPageLoading(true)
            setBreadcrumbs([{ title: "トップ", page: PAGES.MENUS }])
            setCurrentPage(PAGES.MENUS)
            const res = await AccountHttp.getAccountInfo(jwtToken)
            if (res.status === 200) {
                setAccountDetails(res.data)
            } else {
                onFailure()
            }

        } catch (err) {
            onFailure()
        } finally {
            setPageLoading(false)
        }
    }



    const checkSession = useCallback(async (hideMessage) => {
        try {
            const session = await CognitoService.checkSession()
            setJwtToken(session.getIdToken().getJwtToken())
            return true
        } catch (ex) {
            setJwtToken(undefined)
            setAccountDetails(undefined)
            if (!hideMessage) {
                setMessage({ variant: "Warning", msg: "セッションが切れています。再ログインしてください。", show: true })
            }
            setCurrentPage(PAGES.LOGIN)
            setPageLoading(false)
            return false
        }

    }, [])

    useEffect(() => {
        const onInit = async () => {
            setPageLoading(true)
            let result = await checkSession(true)
            if (result) {
                await getAccountDetails()
                setPageLoading(false)
            }
        }

        onInit()

    }, [])

    const login = async (username, password) => {
        try {
            let res = await CognitoService.login(username, password)
            if (res.type === "success") {
                setJwtToken(res.result.getIdToken().getJwtToken())
            } else if (res.type === "totpRequired") {
                setCurrentPage(PAGES.TOTP)
                setLoginDetails({ username, password })
                return
            } else if (res.type === "newPasswordRequired") {
                setCurrentPage(PAGES.CHANGE_TEMP_PASSWORD)
                setUserAttributes(res.userAttributes)
                setCognitoUser(res.cognitoUser)
                return
            }

        } catch (err) {
            setMessage({ variant: "Warning", msg: "ログインできませんでした。", show: true })
            return
        }

        await getAccountDetails()
    }

    const loginWithTotp = async (username, password, totp) => {
        try {
            let res = await CognitoService.loginWithTotp(username, password, totp)
            if (res.type === "success") {
                setJwtToken(res.result.getIdToken().getJwtToken())
                setLoginDetails({})
            }
        } catch (err) {
            setMessage({ variant: "Warning", msg: "入力されたワンタイムパスワードが正しくありません。もう一度確認して入力してください。", show: true })
            return
        }

        await getAccountDetails()
    }

    const completeNewPasswordChallenge = async (password) => {
        try {
            let res = await CognitoService.completeNewPasswordChallenge(password, userAttributes, cognitoUser)
            if (res.type === "success") {
                setJwtToken(res.result.getIdToken().getJwtToken())
                setMessage({ variant: "Success", msg: "初期パスワードが変更完了しました。", show: true })
            } else if (res.type === "totpRequired") {
                setCurrentPage(PAGES.TOTP)
                setLoginDetails({ username, password })
                return
            } else if (res.type === "newPasswordRequired") {
                setMessage({ variant: "Warning", msg: "初期パスワードが変更できませんでした。", show: true })
                return
            }

        } catch (err) {
            setMessage({ variant: "Warning", msg: "初期パスワードが変更できませんでした。", show: true })
            return
        }

        await getAccountDetails()
    }

    const generateConfirmCode = async (username) => {
        try {
            let res = await CognitoService.forgotPassword(username)
            if (res.type === "inputVerificationCode") {
                setCurrentPage(PAGES.RESET_PASSWORD)
                setMessage({ variant: "Success", msg: 'メールアドレス「' + res.data + '」に検証コードを送りました。', show: true })
            }

        } catch (err) {
            setMessage({ variant: "Warning", msg: "確認コード発行できませんでした。", show: true })
        }
    }

    const confirmPassword = async (newPassword, confirmCode) => {
        try {
            let res = await CognitoService.confirmPassword(currentChangeAccountId, newPassword, confirmCode)
            if (res.type === "success") {
                setMessage({ variant: "Success", msg: "パスワードリセット完了しました。", show: true })
                setCurrentPage(PAGES.LOGIN)
            }

        } catch (err) {
            setMessage({ variant: "Warning", msg: "パスワードリセットできませんでした。", show: true })
        }
    }

    const changePassword = async (oldPass, newPass) => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await CognitoService.changePassword(oldPass, newPass)
                if (res.type === "success") {
                    setMessage({ variant: "Success", msg: "パスワード変更完了しました。", show: true })
                }

            } catch (err) {
                setMessage({ variant: "Warning", msg: "パスワード変更できませんでした。", show: true })
            }
        }

    }

    const getTenantList = async () => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await TenantHttp.getTenantList(jwtToken)
                setTenantList(res.data.tenant_data_list)
            } catch (err) {
                setMessage({ variant: "Warning", msg: "組織一覧を取得できませんでした。", show: true })
                setTenantList([])
            }
        }
    }

    const updateTenant = async (tenantData) => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await TenantHttp.updateTenant(jwtToken, tenantData)
                const mappedTenantList = tenantList.map((tenant) => {
                    if (tenant.tenant_id === selectedTenant.tenant_id) {
                        return tenantData
                    } else {
                        return tenant
                    }
                })

                setTenantList(mappedTenantList)
                setMessage({ variant: "Success", msg: "組織の更新が完了しました。", show: true })

            } catch (err) {
                console.log(err)
                setMessage({ variant: "Warning", msg: "組織を更新できませんでした。", show: true })
            }
        }
    }

    const deleteTenant = async (tenantData) => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await TenantHttp.deleteTenant(jwtToken, tenantData)
                const filteredTenantList = tenantList.filter((tenant) => {
                    return tenant.tenant_id !== tenantData.tenant_id
                })

                setTenantList(filteredTenantList)
                setMessage({ variant: "Success", msg: "組織の削除が完了しました。", show: true })
            } catch (err) {
                console.log(err)
                setMessage({ variant: "Warning", msg: "組織を削除できませんでした。", show: true })
            }
        }
    }

    const createTenant = async (tenantData) => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await TenantHttp.createTenant(jwtToken, tenantData)
                console.log(res.data)
                const newTenantList = tenantList.concat([tenantData])

                setTenantList(newTenantList)
                setMessage({ variant: "Success", msg: "組織の登録が完了しました。", show: true })

            } catch (err) {
                console.log(err)
                setMessage({ variant: "Warning", msg: "組織を登録できませんでした。", show: true })
            }
        }
    }

    const getAccountList = async (orgId) => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await AccountHttp.getAccountList(orgId, jwtToken)
                setAccountList(res.data.account_list)
                console.log(res.data.account_list)
            } catch (err) {
                setMessage({ variant: "Warning", msg: "アカウント一覧を取得できませんでした。", show: true })
                setAccountList([])
            }
        }
    }

    const updateAccount = async (accountData) => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await AccountHttp.updateAccount(jwtToken, accountData)
                const mappedAccountList = accountList.map((account) => {
                    if (account.account_id === selectedAccount.account_id) {
                        return accountData
                    } else {
                        return account
                    }
                })

                setAccountList(mappedAccountList)
                setMessage({ variant: "Success", msg: "アカウント変更が完了しました。", show: true })

            } catch (err) {
                console.log(err)
                setMessage({ variant: "Warning", msg: "アカウントを変更できませんでした。", show: true })
            }
        }
    }

    const deleteAccount = async (accountData) => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await AccountHttp.deleteAccount(jwtToken, accountData)
                const filteredAccountList = accountList.filter((account) => {
                    return account.account_id !== accountData.account_list[0].account_id
                })

                setAccountList(filteredAccountList)
                setMessage({ variant: "Success", msg: "アカウント削除が完了しました。", show: true })
            } catch (err) {
                console.log(err)
                setMessage({ variant: "Warning", msg: "アカウントを削除できませんでした。", show: true })
            }
        }
    }

    const createAccount = async (accountData) => {

        let result = await checkSession()

        if (result) {
            try {
                let res = await AccountHttp.createAccount(jwtToken, accountData)
                console.log(res.data)
                const newAccountList = accountList.concat([accountData.account_list[0]])

                setAccountList(newAccountList)
                setMessage({ variant: "Success", msg: "アカウント登録が完了しました。", show: true })

            } catch (err) {
                console.log(err)
                setMessage({ variant: "Warning", msg: "アカウントが登録できませんでした。", show: true })
            }
        }
    }

    const getMfaState = async () => {

        let result = await checkSession()

        if (result) {
            let newAccountDetails = Object.assign({}, accountDetails)
            try {
                let res = await CognitoService.getMfaState()
                if (res.type === "success") {
                    newAccountDetails.mfaState = res.data
                }

            } catch (err) {
                newAccountDetails.mfaState = false
            }

            setAccountDetails(newAccountDetails)
        }
    }

    const associateSoftwareToken = async () => {

        let result = await checkSession()

        if (result) {
            try {
                return await CognitoService.associateSoftwareToken()
            } catch (err) {
                throw new Error("cannot generate QR url")
            }
        }
    }

    const enableMfa = async (mfaConfirmCode) => {

        let result = await checkSession()

        if (result) {
            try {
                return await CognitoService.enableMfa(mfaConfirmCode)
            } catch (err) {
                throw new Error("cannot enable MFA")
            }
        }
    }

    const disableMfa = async () => {

        let result = await checkSession()

        if (result) {
            try {
                return await CognitoService.disableMfa()
            } catch (err) {
                throw new Error("cannot disable MFA")
            }
        }
    }

    return (

        <IndexContext.Provider value={{
            jwtToken, login, message, setMessage, loginWithTotp, loginDetails, setLoginDetails, accountDetails, setAccountDetails, tenantList, getTenantList, selectedTenant, setSelectedTenant, updateTenant, deleteTenant, createTenant, breadcrumbs, setBreadcrumbs, pageLoading, setPageLoading, currentPage, setCurrentPage, accountList, setAccountList, getAccountList, updateAccount, deleteAccount, selectedAccount, setSelectedAccount, createAccount, setJwtToken, generateConfirmCode, confirmPassword, setCurrentChangeAccountId, changePassword, getMfaState, currentChangeAccountId, associateSoftwareToken, enableMfa, disableMfa, userAttributes, cognitoUser, completeNewPasswordChallenge
        }}>
            {children}
        </IndexContext.Provider>

    );
};
